import { useState, useEffect, useCallback } from 'react';
import { vaultApi } from '@/api/vault';
import { useVault } from './VaultContext';
import {
  decryptToBuffer,
  deriveSendKey,
  decryptCipherString,
  base64urlEncode,
  toArrayBuffer,
} from '@/lib/vault-crypto';
import { Button } from '@/components/ui/button';
import { Copy, Trash2, Check, Loader2, Send, ChevronDown, ChevronUp, Lock, Eye, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface DecryptedSend {
  id: string;
  name: string;
  accessCount: number;
  maxAccessCount: number | null;
  revisionDate: string;
  deletionDate: string;
  expirationDate: string | null;
  disabled: boolean;
  sendUrl: string;
  sendSecret: Uint8Array; // for building full URL
  isExpired: boolean;
  isFullyViewed: boolean; // accessCount >= maxAccessCount (e.g. 1/1, 2/2)
}

export function SendListTab() {
  const { accessToken, getUserKey } = useVault();
  const [sends, setSends] = useState<DecryptedSend[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadSends = useCallback(async () => {
    if (!accessToken) return;
    const userKey = getUserKey();
    if (!userKey) return;

    setIsLoading(true);
    try {
      const rawSends = await vaultApi.listSends(accessToken);
      const now = new Date();

      const decrypted: DecryptedSend[] = [];
      for (const raw of rawSends) {
        try {
          // Decrypt send secret: send.key → decryptToBuffer → raw 16 bytes
          const sendSecretBuf = await decryptToBuffer(raw.key as string, userKey);
          const sendSecret = new Uint8Array(sendSecretBuf);

          // Derive send key for decryption
          const sendKey = await deriveSendKey(sendSecret, { decryptable: true });

          // Decrypt name
          const name = raw.name ? await decryptCipherString(raw.name as string, sendKey) : '(без имени)';

          const expDate = raw.expirationDate ? new Date(raw.expirationDate as string) : null;
          const delDate = new Date(raw.deletionDate as string);
          const isExpired = (expDate && expDate < now) || delDate < now;

          decrypted.push({
            id: raw.id as string,
            name,
            accessCount: (raw.accessCount as number) || 0,
            maxAccessCount: (raw.maxAccessCount as number) || null,
            revisionDate: raw.revisionDate as string,
            deletionDate: raw.deletionDate as string,
            expirationDate: raw.expirationDate as string | null,
            disabled: (raw.disabled as boolean) || false,
            sendUrl: raw.sendUrl as string,
            sendSecret,
            isExpired: !!isExpired,
            isFullyViewed: !!(raw.maxAccessCount && (raw.accessCount as number) >= (raw.maxAccessCount as number)),
          });
        } catch {
          // Skip sends we can't decrypt
        }
      }

      // Sort by revision date, newest first
      decrypted.sort((a, b) => new Date(b.revisionDate).getTime() - new Date(a.revisionDate).getTime());
      setSends(decrypted);
    } catch {
      toast.error('Не удалось загрузить Send-ы');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, getUserKey]);

  useEffect(() => {
    loadSends();
  }, [loadSends]);

  const handleCopy = async (send: DecryptedSend) => {
    const urlKey = base64urlEncode(toArrayBuffer(send.sendSecret));
    const fullUrl = `${send.sendUrl}/${urlKey}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(send.id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.info('Скопируйте вручную: ' + fullUrl);
    }
  };

  const handleDelete = async (sendId: string) => {
    if (!accessToken) return;
    try {
      await vaultApi.deleteSend(accessToken, sendId);
      setSends((prev) => prev.filter((s) => s.id !== sendId));
      toast.success('Send удалён');
    } catch {
      toast.error('Не удалось удалить');
    }
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Загрузка...
      </div>
    );
  }

  if (sends.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        <Send className="w-8 h-8 mx-auto mb-2 opacity-30" />
        Нет отправленных Send-ов
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sends.map((send) => {
        const isExpanded = expandedId === send.id;
        return (
          <div
            key={send.id}
            className={`rounded-lg border ${
              send.isExpired || send.disabled ? 'bg-card opacity-50' :
              send.isFullyViewed ? 'border-orange-500/30 bg-card' : 'bg-card'
            }`}
          >
            {/* Header — clickable */}
            <button
              className="w-full p-3 text-left"
              onClick={() => setExpandedId(isExpanded ? null : send.id)}
            >
              <div className="flex items-start gap-2">
                <Send className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{send.name}</div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span>{formatDate(send.revisionDate)}</span>
                    <span className={send.isFullyViewed ? 'text-orange-600 font-medium' : ''}>
                      {send.accessCount}{send.maxAccessCount ? `/${send.maxAccessCount}` : ''} просм.
                    </span>
                    {send.isExpired && <span className="text-red-500">истёк</span>}
                    {send.disabled && <span className="text-yellow-600">откл.</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0 items-center">
                  {!send.isExpired && !send.disabled && !send.isFullyViewed && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => { e.stopPropagation(); handleCopy(send); }}
                    >
                      {copied === send.id ? (
                        <Check className="h-3.5 w-3.5 text-green-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </div>
              </div>
            </button>

            {/* Details — expandable */}
            {isExpanded && (
              <div className="px-3 pb-3 pt-0 border-t space-y-2 text-xs">
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    <span>Создан</span>
                  </div>
                  <div>{send.revisionDate ? formatDate(send.revisionDate) : '—'}</div>

                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>Истекает</span>
                  </div>
                  <div>{send.expirationDate ? formatDate(send.expirationDate) : 'Никогда'}</div>

                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Trash2 className="w-3 h-3" />
                    <span>Удаление</span>
                  </div>
                  <div>{formatDate(send.deletionDate)}</div>

                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Eye className="w-3 h-3" />
                    <span>Просмотры</span>
                  </div>
                  <div>
                    {send.accessCount} {send.maxAccessCount ? `из ${send.maxAccessCount}` : '(без лимита)'}
                  </div>

                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Lock className="w-3 h-3" />
                    <span>Пароль</span>
                  </div>
                  <div>{send.disabled ? 'Отключён' : 'Нет'}</div>
                </div>

                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => handleDelete(send.id)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Удалить Send
                </Button>
              </div>
            )}
          </div>
        );
      })}
      <Button variant="ghost" size="sm" className="w-full text-xs" onClick={loadSends}>
        Обновить
      </Button>
    </div>
  );
}
